import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaFileInvoice, 
  FaChartBar, 
  FaArrowRight, 
  FaCheckCircle, 
  FaClock, 
  FaExclamationTriangle,
  FaShieldAlt,
  FaUsers,
  FaCalendarAlt,
  FaDownload,
 
  FaRocket,
  FaSync,

  FaWallet,
  FaPercentage,
  FaChartLine,
  FaBuilding,
 
} from 'react-icons/fa';

const FbrInvoicePost = () => {
  const navigate = useNavigate();
  const [hoverBox, setHoverBox] = useState(null);
  const [hoverStat, setHoverStat] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    fiscal_year: {
      start_date: '',
      end_date: '',
      display: ''
    },
    summary: {
      total_invoices: 0,
      total_sales: 0,
      total_tax: 0,
      total_discount: 0,
      total_paid: 0,
      net_sales: 0
    },
    fbr_push_status: {
      pushed_to_fbr: 0,
      pending_push: 0,
      push_percentage: 0,
      pending_percentage: 0
    },
    fbr_push_amounts: {
      pushed_sales_amount: 0,
      pushed_tax_amount: 0,
      pending_sales_amount: 0,
      pending_tax_amount: 0
    },
    current_year_totals: {
      year: '',
      total_invoices: 0,
      total_sales: 0,
      total_tax: 0,
      pushed_to_fbr: 0,
      pending_push: 0
    },
    monthly_breakdown: [],
    daily_breakdown: [],
    top_customers: []
  });

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/fbr/statistics');
      
      if (response.data.status === 200 && response.data.success) {
        setDashboardData(response.data.data);
      } else {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBoxClick = (boxName) => {
    console.log(`Clicked on ${boxName}`);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

 

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get month name
  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  // Styles
  const styles = {
    container: {
      padding: '30px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      background: '#f0f2f5',
      minHeight: '100vh',
    },
    header: {
      background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 100%)',
      borderRadius: '24px',
      padding: '40px 50px',
      marginBottom: '35px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      position: 'relative',
      overflow: 'hidden',
    },
    headerGradient: {
      position: 'absolute',
      top: '-50%',
      right: '-10%',
      width: '500px',
      height: '500px',
      background: 'radial-gradient(circle, rgba(52, 152, 219, 0.15) 0%, transparent 70%)',
      borderRadius: '50%',
    },
    headerGradient2: {
      position: 'absolute',
      bottom: '-30%',
      left: '-5%',
      width: '400px',
      height: '400px',
      background: 'radial-gradient(circle, rgba(46, 204, 113, 0.1) 0%, transparent 70%)',
      borderRadius: '50%',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      zIndex: 1,
      flexWrap: 'wrap',
      gap: '20px',
    },
    headerLeft: {
      flex: 1,
    },
    headerTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '8px',
    },
    headerIcon: {
      color: '#3498db',
      fontSize: '2.8rem',
      filter: 'drop-shadow(0 2px 10px rgba(52, 152, 219, 0.3))',
    },
    headerSubtitle: {
      color: '#b0bec5',
      fontSize: '1.05rem',
      marginTop: '5px',
      opacity: 0.9,
    },
    headerRight: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
    },
    headerBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      padding: '10px 20px',
      borderRadius: '50px',
      color: '#fff',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '500',
    },
    badgeDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#2ecc71',
      animation: 'pulse 2s infinite',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '20px',
      marginBottom: '35px',
    },
    statCard: {
      background: 'white',
      padding: '25px',
      borderRadius: '20px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
    },
    statIconWrapper: {
      width: '48px',
      height: '48px',
      borderRadius: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.4rem',
      marginBottom: '12px',
    },
    statLabel: {
      fontSize: '0.85rem',
      color: '#7f8c8d',
      fontWeight: '500',
      marginBottom: '6px',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2c3e50',
      marginBottom: '4px',
    },
    statChange: {
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    cardsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '30px',
      marginBottom: '35px',
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      padding: '40px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    cardDecoration: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      opacity: 0.05,
      transform: 'translate(30%, -30%)',
    },
    cardIconWrapper: {
      width: '72px',
      height: '72px',
      borderRadius: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
      fontSize: '2rem',
    },
    cardTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      color: '#2c3e50',
      marginBottom: '10px',
    },
    cardDescription: {
      color: '#7f8c8d',
      lineHeight: '1.7',
      marginBottom: '25px',
      fontSize: '0.95rem',
    },
    cardFeatures: {
      display: 'flex',
      gap: '20px',
      marginBottom: '25px',
      flexWrap: 'wrap',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.85rem',
      color: '#555',
    },
    cardButton: {
      padding: '14px 32px',
      borderRadius: '50px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      textDecoration: 'none',
    },
    activitySection: {
      background: 'white',
      borderRadius: '24px',
      padding: '30px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      marginTop: '35px',
    },
    activityHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '25px',
      paddingBottom: '15px',
      borderBottom: '2px solid #f0f2f5',
    },
    activityTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#2c3e50',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    viewAllBtn: {
      background: 'transparent',
      border: 'none',
      color: '#3498db',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '8px 20px',
      borderRadius: '20px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    activityList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    activityItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      background: '#f8f9fa',
      borderRadius: '16px',
      transition: 'all 0.3s ease',
      gap: '15px',
    },
    activityIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    activityInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    activityName: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    activityDetails: {
      display: 'flex',
      gap: '15px',
      fontSize: '0.8rem',
      color: '#95a5a6',
    },
    activityStatus: {
      padding: '4px 14px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'capitalize',
    },
    activityActions: {
      display: 'flex',
      gap: '8px',
    },
    actionBtn: {
      width: '36px',
      height: '36px',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.9rem',
      background: 'white',
      color: '#555',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    quickActions: {
      display: 'flex',
      gap: '15px',
      flexWrap: 'wrap',
      marginTop: '30px',
      justifyContent: 'center',
    },
    quickActionBtn: {
      padding: '14px 28px',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
    },
    fiscalYearBanner: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '16px',
      padding: '20px 30px',
      marginBottom: '30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '15px',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    },
    fiscalYearText: {
      color: 'white',
      fontSize: '1.1rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    fiscalYearLabel: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '0.9rem',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      background: '#e0e0e0',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '5px',
    },
    progressFill: {
      height: '100%',
      borderRadius: '4px',
      transition: 'width 0.3s ease',
    },
    sectionTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    grid2Col: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px',
      marginBottom: '20px',
    },
    infoCard: {
      background: '#f8f9fa',
      padding: '15px 20px',
      borderRadius: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    infoLabel: {
      color: '#7f8c8d',
      fontSize: '0.85rem',
    },
    infoValue: {
      fontWeight: '600',
      fontSize: '1.1rem',
      color: '#2c3e50',
    },
  };

  // Loading shimmer component
  const Shimmer = () => (
    <div style={{
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '1000px 100%',
      animation: 'shimmer 2s infinite',
      height: '100%',
      minHeight: '100px',
      borderRadius: '12px',
    }} />
  );

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes countUp {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        .fade-in {
          animation: fadeIn 0.6s ease forwards;
        }
        .slide-up {
          animation: slideUp 0.6s ease forwards;
        }
        .count-up {
          animation: countUp 0.4s ease forwards;
        }
        .shimmer {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>

      {/* Header Section */}
      <div style={styles.header} className="fade-in">
        <div style={styles.headerGradient} />
        <div style={styles.headerGradient2} />
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <h1 style={styles.headerTitle}>
              <FaFileInvoice style={styles.headerIcon} />
              FBR Management Dashboard
            </h1>
            <p style={styles.headerSubtitle}>
              Manage your FBR invoicing and reporting efficiently
            </p>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.headerBadge}>
              <span style={styles.badgeDot} />
              System Active
            </div>
            <div style={styles.headerBadge}>
              <FaCalendarAlt />
              {new Date().toLocaleDateString('en-PK', { 
                weekday: 'short', 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </div>
            <div style={styles.headerBadge}>
              <FaBuilding />
              FY {dashboardData.fiscal_year.display || '2026-2027'}
            </div>
          </div>
        </div>
      </div>

      {/* Fiscal Year Banner */}
      <div style={styles.fiscalYearBanner} className="slide-up">
        <div style={styles.fiscalYearText}>
          <FaCalendarAlt />
          <span>
            <span style={styles.fiscalYearLabel}>Fiscal Year</span>
            <br />
            <strong>{dashboardData.fiscal_year.display || '2026-2027'}</strong>
          </span>
          <span style={{ marginLeft: '10px', fontSize: '0.85rem', opacity: 0.8 }}>
            ({dashboardData.fiscal_year.start_date || '2026-07-01'} to {dashboardData.fiscal_year.end_date || '2027-06-30'})
          </span>
        </div>
        <div style={{ flex: 1, maxWidth: '300px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'white', fontSize: '0.85rem', marginBottom: '5px' }}>
            <span>Push Progress</span>
            <span>{dashboardData.fbr_push_status.push_percentage || 0}%</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${dashboardData.fbr_push_status.push_percentage || 0}%`,
              background: 'linear-gradient(90deg, #2ecc71, #27ae60)',
            }} />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid} className="slide-up">
        {isLoading ? (
          <>
            <div style={styles.statCard}><Shimmer /></div>
            <div style={styles.statCard}><Shimmer /></div>
            <div style={styles.statCard}><Shimmer /></div>
            <div style={styles.statCard}><Shimmer /></div>
          </>
        ) : (
          <>
            <div 
              style={{
                ...styles.statCard,
                ...(hoverStat === 'total' ? { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)' } : {})
              }}
              onMouseEnter={() => setHoverStat('total')}
              onMouseLeave={() => setHoverStat(null)}
            >
              <div style={{ ...styles.statIconWrapper, background: '#e3f2fd', color: '#1976d2' }}>
                <FaFileInvoice />
              </div>
              <div style={styles.statLabel}>Total Invoices</div>
              <div style={styles.statValue}>{dashboardData.summary.total_invoices.toLocaleString()}</div>
              <div style={{ ...styles.statChange, color: '#27ae60' }}>
                <FaCheckCircle size={12} /> {dashboardData.fbr_push_status.push_percentage || 0}% pushed
              </div>
            </div>

            <div 
              style={{
                ...styles.statCard,
                ...(hoverStat === 'sales' ? { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)' } : {})
              }}
              onMouseEnter={() => setHoverStat('sales')}
              onMouseLeave={() => setHoverStat(null)}
            >
              <div style={{ ...styles.statIconWrapper, background: '#e8f5e9', color: '#2e7d32' }}>
                <FaWallet />
              </div>
              <div style={styles.statLabel}>Total Sales</div>
              <div style={styles.statValue}>{formatCurrency(dashboardData.summary.total_sales)}</div>
              <div style={{ ...styles.statChange, color: '#2e7d32' }}>
                <FaChartLine size={12} /> Net Sales: {formatCurrency(dashboardData.summary.net_sales)}
              </div>
            </div>

            <div 
              style={{
                ...styles.statCard,
                ...(hoverStat === 'tax' ? { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)' } : {})
              }}
              onMouseEnter={() => setHoverStat('tax')}
              onMouseLeave={() => setHoverStat(null)}
            >
              <div style={{ ...styles.statIconWrapper, background: '#fff3e0', color: '#e67e22' }}>
                <FaPercentage />
              </div>
              <div style={styles.statLabel}>Total Tax</div>
              <div style={styles.statValue}>{formatCurrency(dashboardData.summary.total_tax)}</div>
              <div style={{ ...styles.statChange, color: '#e67e22' }}>
                <FaExclamationTriangle size={12} /> Tax Rate: {dashboardData.summary.total_sales > 0 ? ((dashboardData.summary.total_tax / dashboardData.summary.total_sales) * 100).toFixed(2) : 0}%
              </div>
            </div>

            <div 
              style={{
                ...styles.statCard,
                ...(hoverStat === 'fbr' ? { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)' } : {})
              }}
              onMouseEnter={() => setHoverStat('fbr')}
              onMouseLeave={() => setHoverStat(null)}
            >
              <div style={{ ...styles.statIconWrapper, background: '#f3e5f5', color: '#7b1fa2' }}>
                <FaShieldAlt />
              </div>
              <div style={styles.statLabel}>FBR Status</div>
              <div style={styles.statValue}>
                {dashboardData.fbr_push_status.pushed_to_fbr.toLocaleString()}
                <span style={{ fontSize: '1rem', color: '#95a5a6' }}>
                  / {dashboardData.fbr_push_status.pending_push.toLocaleString()}
                </span>
              </div>
              <div style={{ ...styles.statChange, color: '#7b1fa2' }}>
                <FaCheckCircle size={12} /> Pushed / Pending
              </div>
            </div>
          </>
        )}
      </div>

      {/* FBR Push Amounts Summary */}
      {!isLoading && (
        <div style={styles.grid2Col} className="slide-up">
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <div style={styles.sectionTitle}>
              <FaCheckCircle color="#27ae60" />
              Pushed to FBR
            </div>
            <div style={styles.infoCard}>
              <span style={styles.infoLabel}>Sales Amount</span>
              <span style={{ ...styles.infoValue, color: '#27ae60' }}>
                {formatCurrency(dashboardData.fbr_push_amounts.pushed_sales_amount)}
              </span>
            </div>
            <div style={styles.infoCard}>
              <span style={styles.infoLabel}>Tax Amount</span>
              <span style={{ ...styles.infoValue, color: '#27ae60' }}>
                {formatCurrency(dashboardData.fbr_push_amounts.pushed_tax_amount)}
              </span>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <div style={styles.sectionTitle}>
              <FaClock color="#f39c12" />
              Pending Push
            </div>
            <div style={styles.infoCard}>
              <span style={styles.infoLabel}>Sales Amount</span>
              <span style={{ ...styles.infoValue, color: '#f39c12' }}>
                {formatCurrency(dashboardData.fbr_push_amounts.pending_sales_amount)}
              </span>
            </div>
            <div style={styles.infoCard}>
              <span style={styles.infoLabel}>Tax Amount</span>
              <span style={{ ...styles.infoValue, color: '#f39c12' }}>
                {formatCurrency(dashboardData.fbr_push_amounts.pending_tax_amount)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Cards Section */}
      <div style={styles.cardsContainer} className="slide-up">
        {/* FBR Invoicing Card */}
        <div 
          style={{
            ...styles.card,
            ...(hoverBox === 'invoicing' ? { transform: 'translateY(-8px)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)' } : {}),
          }}
          onMouseEnter={() => setHoverBox('invoicing')}
          onMouseLeave={() => setHoverBox(null)}
          onClick={() => handleBoxClick('FBR Invoicing')}
        >
          <div style={{ 
            ...styles.cardDecoration, 
            background: 'linear-gradient(135deg, #3498db, #2980b9)',
          }} />
          <div style={{ ...styles.cardIconWrapper, background: '#e3f2fd', color: '#1976d2' }}>
            <FaFileInvoice />
          </div>
          <h2 style={styles.cardTitle}>FBR Invoicing</h2>
          <p style={styles.cardDescription}>
            Generate, manage, and track FBR compliant invoices for your business transactions
          </p>
          <div style={styles.cardFeatures}>
            <span style={styles.featureItem}>
              <FaCheckCircle color="#27ae60" size={14} /> Real-time validation
            </span>
            <span style={styles.featureItem}>
              <FaShieldAlt color="#3498db" size={14} /> Secure submission
            </span>
            <span style={styles.featureItem}>
              <FaSync color="#f39c12" size={14} /> Auto-retry on failure
            </span>
          </div>
          <button
            style={{
              ...styles.cardButton,
              background: 'linear-gradient(135deg, #3498db, #2980b9)',
              color: 'white',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleNavigation('/pushfbrinvoice');
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Go to FBR Invoicing 
            <FaArrowRight style={{ transition: 'transform 0.3s ease' }} />
          </button>
        </div>

        {/* Reporting Card */}
        <div 
          style={{
            ...styles.card,
            ...(hoverBox === 'reporting' ? { transform: 'translateY(-8px)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)' } : {}),
          }}
          onMouseEnter={() => setHoverBox('reporting')}
          onMouseLeave={() => setHoverBox(null)}
          onClick={() => handleBoxClick('Reporting')}
        >
          <div style={{ 
            ...styles.cardDecoration, 
            background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
          }} />
          <div style={{ ...styles.cardIconWrapper, background: '#e8f5e9', color: '#2e7d32' }}>
            <FaChartBar />
          </div>
          <h2 style={styles.cardTitle}>Reporting</h2>
          <p style={styles.cardDescription}>
            Access comprehensive reports, analytics, and insights for your business performance
          </p>
          <div style={styles.cardFeatures}>
            <span style={styles.featureItem}>
              <FaChartBar color="#27ae60" size={14} /> Real-time analytics
            </span>
            <span style={styles.featureItem}>
              <FaDownload color="#3498db" size={14} /> Export reports
            </span>
            <span style={styles.featureItem}>
              <FaUsers color="#9b59b6" size={14} /> Team insights
            </span>
          </div>
          <button 
            style={{
              ...styles.cardButton,
              background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
              color: 'white',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(4px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(46, 204, 113, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            View Reports 
            <FaArrowRight style={{ transition: 'transform 0.3s ease' }} />
          </button>
        </div>
      </div>

      {/* Monthly Breakdown Section */}
      {!isLoading && dashboardData.monthly_breakdown.length > 0 && (
        <div style={styles.activitySection} className="slide-up">
          <div style={styles.activityHeader}>
            <div style={styles.activityTitle}>
              <FaChartBar color="#3498db" />
              Monthly Breakdown - {dashboardData.fiscal_year.display}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#555' }}>Month</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Invoices</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Sales</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Tax</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Pushed</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Push %</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.monthly_breakdown.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{getMonthName(item.month)} {item.year}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{item.invoice_count}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(item.monthly_sales)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#e67e22' }}>
                      {formatCurrency(item.monthly_tax)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{ 
                        background: item.pushed_count === item.invoice_count ? '#e8f5e9' : '#fff3e0',
                        color: item.pushed_count === item.invoice_count ? '#27ae60' : '#f39c12',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600'
                      }}>
                        {item.pushed_count} / {item.invoice_count}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '6px', 
                          background: '#e0e0e0', 
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${item.push_percentage}%`,
                            height: '100%',
                            background: item.push_percentage === 100 ? '#27ae60' : '#3498db',
                            borderRadius: '3px',
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                        <span style={{ fontSize: '0.8rem', fontWeight: '600' }}>{item.push_percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Customers Section */}
      {!isLoading && dashboardData.top_customers.length > 0 && (
        <div style={styles.activitySection} className="slide-up">
          <div style={styles.activityHeader}>
            <div style={styles.activityTitle}>
              <FaUsers color="#9b59b6" />
              Top Customers by Sales
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#555' }}>#</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#555' }}>Customer Name</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Invoices</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Total Sales</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Total Tax</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Pushed</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.top_customers.map((customer, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', fontWeight: '600', color: '#3498db' }}>{index + 1}</td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{customer.customer_name}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{customer.invoice_count}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#2c3e50' }}>
                      {formatCurrency(customer.total_sales)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#e67e22' }}>
                      {formatCurrency(customer.total_tax)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{ 
                        background: customer.pushed_count === customer.invoice_count ? '#e8f5e9' : '#fff3e0',
                        color: customer.pushed_count === customer.invoice_count ? '#27ae60' : '#f39c12',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600'
                      }}>
                        {customer.pushed_count} / {customer.invoice_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={styles.quickActions} className="slide-up">
        <button 
          style={{ ...styles.quickActionBtn, background: '#3498db', color: 'white' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}
          onClick={() => handleNavigation('/pushfbrinvoice')}
        >
          <FaRocket /> Quick Push Invoice
        </button>
       
        <button 
          style={{ ...styles.quickActionBtn, background: '#e67e22', color: 'white' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(230, 126, 34, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}
          onClick={fetchDashboardData}
        >
          <FaSync /> Refresh Data
        </button>
      </div>
    </div>
  );
};

export default FbrInvoicePost;